// @ts-nocheck
// Verbatim port of design/Login.html. Only changes from the design file:
//   1. Embedded the design's <style> block as a React-scoped <style> element
//      so the .shell grid, .g-input, .btn-primary, etc. render exactly as
//      the design file.
//   2. The "Email me a sign-in code" button now calls /v1/auth/sign-in.
//   3. The "Verify & log in" button calls /v1/auth/verify-code and stores
//      the resulting JWT via useAuth().
import React, { useState, useEffect, useRef } from "react";
import * as LucideIcons from "lucide-react";
import { useNavigate } from "react-router-dom";
import { auth as authApi, applications, API_BASE_URL } from "../lib/api";
import { useAuth } from "../lib/auth";

const DESIGN_CSS = `
  .signin-root {
    --surface:#fffdf7; --soft:#faf5ea; --hair:#efe6d4; --border:#e6dcc3; --ring-muted:#cbb993;
    --accent:#b04e34; --accent-700:#83391f; --accent-50:#fbf1ec;
    --success:#6a734f; --success-700:#454d34; --success-50:#dfe4d3;
    --forest:#243528; --forest-2:#2e4233; --moss:#7a8463; --sage-50:#eef1e7;
    --ink:#1f1d18; --ink-1:#3b372e; --ink-2:#5c574b; --ink-3:#5c5746;
    background:#ece2cf; min-height:100vh; display:flex; align-items:center; justify-content:center; padding:18px;
    font-family:'IBM Plex Sans',system-ui,sans-serif; color:#1f1d18; -webkit-font-smoothing:antialiased;
  }
  .signin-root .font-display { font-family:'Instrument Serif',serif; letter-spacing:-0.01em; }
  .signin-root .shell {
    width:min(960px,98vw); height:min(620px,94vh);
    display:grid; grid-template-columns:0.92fr 1.08fr;
    border-radius:22px; overflow:hidden; border:1px solid var(--border);
    box-shadow:0 1px 2px rgba(31,29,24,0.04), 0 40px 90px -48px rgba(31,29,24,0.45);
  }
  @media (max-width:760px){
    .signin-root .shell{grid-template-columns:1fr;}
    .signin-root .leftpanel{display:none;}
  }
  .signin-root .g-input { width:100%; background:#fff; border:1.5px solid var(--border); border-radius:12px; padding:12px 14px; font-size:15px; color:var(--ink); transition:border-color 150ms, box-shadow 150ms; }
  .signin-root .g-input:focus { outline:none; border-color:var(--accent); box-shadow:0 0 0 4px rgba(176,78,52,0.10); }
  .signin-root .btn-primary { background:var(--accent); color:#fff; border-radius:12px; font-weight:600; transition:background 160ms, transform 120ms; }
  .signin-root .btn-primary:hover { background:var(--accent-700); transform:translateY(-1px); }
  .signin-root .btn-sso { background:#fff; border:1.5px solid var(--border); border-radius:12px; font-weight:500; color:var(--ink-1); transition:border-color 150ms; }
  .signin-root .btn-sso:hover { border-color:var(--ring-muted); }
  .signin-root .focus-ring:focus-visible { outline:2px solid var(--accent); outline-offset:2px; border-radius:10px; }
  .signin-root .quiet-link { position:relative; cursor:pointer; }
  .signin-root .quiet-link::after { content:''; position:absolute; left:0; right:0; bottom:-1px; height:1px; background:currentColor; opacity:.35; }
  .signin-root .quiet-link:hover::after { opacity:.8; }
  .signin-root .fade { animation:signin-fade 320ms ease both; }
  @keyframes signin-fade { from{opacity:.5;transform:translateY(6px)} to{opacity:1;transform:none} }
`;

function I({ name, cls = "w-4 h-4" }) {
  const pascal = name.split("-").map(s => s[0].toUpperCase() + s.slice(1)).join("");
  const Icon = LucideIcons[pascal];
  return Icon ? <Icon className={cls} /> : null;
}

// Derive a sensible default workspace name from the email so first-time
// signups can hit /sign-in without filling out a separate workspace field.
function workspaceFromEmail(email) {
  const local = (email.split("@")[0] || "").replace(/[+.].*$/, "");
  const human = local.replace(/[._-]+/g, " ").trim();
  return human ? `${human}'s petition` : "My petition";
}

export default function SignIn() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [step, setStep] = useState("email"); // email | code
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  // "Continue with Google" state. googleBusy hides re-clicks while the
  // popup is open; googleAvailable is set on mount by a /sign-in-url
  // probe — if the backend returns 503 oauth_not_configured (Google
  // creds aren't set on the deployment), the button stays disabled
  // with a tooltip rather than throwing on first click.
  const [googleBusy, setGoogleBusy] = useState(false);
  const [googleAvailable, setGoogleAvailable] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/v1/auth/google/sign-in-url`);
        if (!cancelled) setGoogleAvailable(res.ok);
      } catch {
        if (!cancelled) setGoogleAvailable(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  // "Signed out" confirmation banner — shown briefly after auth.signOut() so
  // the user can see the action took effect. Reads a short-lived marker
  // sessionStorage key written by signOut, then clears it so a refresh
  // doesn't re-show the banner.
  const [showSignedOut, setShowSignedOut] = useState(() => {
    try {
      const t = Number(sessionStorage.getItem("scholarpetition.signedOutAt") || 0);
      const recent = t > 0 && Date.now() - t < 10000;
      if (recent) sessionStorage.removeItem("scholarpetition.signedOutAt");
      return recent;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (!showSignedOut) return;
    const id = window.setTimeout(() => setShowSignedOut(false), 5000);
    return () => window.clearTimeout(id);
  }, [showSignedOut]);
  const [error, setError] = useState("");
  const valid = /\S+@\S+\.\S+/.test(email);
  const codeRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  const codeFull = code.every(d => d !== "");

  const setDigit = (i, v) => {
    const d = v.replace(/\D/g, "").slice(-1);
    setCode(c => { const n = [...c]; n[i] = d; return n; });
    if (d && i < 5) codeRefs[i + 1].current && codeRefs[i + 1].current.focus();
  };
  const onCodeKey = (i, e) => {
    if (e.key === "Backspace" && !code[i] && i > 0) codeRefs[i - 1].current && codeRefs[i - 1].current.focus();
  };
  const onCodePaste = (i, e) => {
    const text = (e.clipboardData || window.clipboardData)?.getData("text") || "";
    const digits = text.replace(/\D/g, "").slice(0, 6 - i);
    if (!digits) return;
    e.preventDefault();
    setCode(c => {
      const n = [...c];
      for (let k = 0; k < digits.length && i + k < 6; k++) n[i + k] = digits[k];
      return n;
    });
    const last = Math.min(i + digits.length, 5);
    codeRefs[last].current && codeRefs[last].current.focus();
  };

  const continueWithGoogle = async () => {
    if (googleBusy || !googleAvailable) return;
    setError("");
    setGoogleBusy(true);
    let popup: Window | null = null;
    try {
      // Fetch the auth URL fresh per click — the state token embedded
      // in it is short-lived (10 min) and single-use.
      const res = await fetch(`${API_BASE_URL}/v1/auth/google/sign-in-url`);
      if (!res.ok) throw new Error("Google sign-in is not configured.");
      const { auth_url } = await res.json();
      // Center a small popup. Pop-up blockers usually allow this since
      // it's the direct result of a user click.
      const w = 480, h = 640;
      const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
      const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
      popup = window.open(
        auth_url,
        "visagenius-google-signin",
        `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=no`,
      );
      if (!popup) {
        throw new Error("Pop-up blocked. Allow pop-ups for this site and try again.");
      }
      // Listen for the postMessage from /v1/auth/google/sign-in-callback.
      // Match origin (same as window.location.origin — the backend sets
      // targetOrigin to opener.location.origin) and the source tag.
      const onMessage = (e: MessageEvent) => {
        if (e.origin !== window.location.origin) return;
        const data = e.data;
        if (!data || data.source !== "visagenius:sign-in") return;
        window.removeEventListener("message", onMessage);
        window.clearInterval(closedPoller);
        setGoogleBusy(false);
        if (!data.ok) {
          setError(
            data.reason === "email_not_verified"
              ? "Your Google account's email isn't verified yet."
              : data.reason === "account_disabled"
                ? "This account is disabled. Contact your workspace admin."
                : "Google sign-in failed. Try again or use email below."
          );
          return;
        }
        if (data.token) {
          signIn(data.token);
          (async () => {
            try {
              localStorage.setItem("conv-step", "1");
              const apps = await applications.list();
              const list = Array.isArray(apps) ? apps : (apps as { items?: unknown[] })?.items;
              if (Array.isArray(list) && list.length > 0) {
                const forkSeen = (() => {
                  try { return localStorage.getItem("attorney-fork-seen") === "1"; } catch { return false; }
                })();
                localStorage.setItem("conv-step", forkSeen ? "6" : "5");
              }
            } catch { /* fall through to default landing */ }
            navigate("/scholar-path");
          })();
        }
      };
      window.addEventListener("message", onMessage);
      // Belt-and-braces: if the user closes the popup without
      // completing, clear the busy state so they can retry.
      const closedPoller = window.setInterval(() => {
        if (popup && popup.closed) {
          window.clearInterval(closedPoller);
          window.removeEventListener("message", onMessage);
          setGoogleBusy(false);
        }
      }, 500);
    } catch (e: any) {
      try { popup?.close(); } catch {}
      setGoogleBusy(false);
      setError(e?.message || "Google sign-in failed. Please try again.");
    }
  };

  const requestCode = async () => {
    if (!valid || sending) return;
    setError("");
    setSending(true);
    try {
      await authApi.signIn({ email: email.toLowerCase().trim(), workspace_name: workspaceFromEmail(email) });
      setStep("code");
    } catch (e) {
      setError(e?.message || "Could not send code. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async () => {
    if (!codeFull || verifying) return;
    setError("");
    setVerifying(true);
    try {
      const tok = await authApi.verifyCode({ email: email.toLowerCase().trim(), code: code.join("") });
      signIn(tok);
      // Default new users to step 1 (EligibilityScholar — Google Scholar URL
      // prompt). Returning users with saved work jump ahead: to step 5
      // (AttorneyFirst — Talk-to-attorney vs Start-building fork) on the
      // first sign-in, then straight to step 6 (CasePlan) once they've
      // already chosen a path through the fork (attorney-fork-seen flag).
      try {
        localStorage.setItem("conv-step", "1");
      } catch {}
      try {
        const apps = await applications.list();
        const list = Array.isArray(apps) ? apps : (apps as { items?: unknown[] })?.items;
        if (Array.isArray(list) && list.length > 0) {
          const forkSeen = (() => {
            try { return localStorage.getItem("attorney-fork-seen") === "1"; } catch { return false; }
          })();
          localStorage.setItem("conv-step", forkSeen ? "6" : "5");
        }
      } catch {
        // Best-effort. conv-step stays at 1 (set above) — user lands at
        // the Scholar URL prompt, which is the right default for a new
        // account anyway.
      }
      navigate("/scholar-path");
    } catch (e) {
      setError(e?.message || "Invalid or expired code.");
    } finally {
      setVerifying(false);
    }
  };

  // Auto-submit once the user fills the 6th digit.
  useEffect(() => {
    if (step === "code" && codeFull && !verifying) verifyCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeFull, step]);

  return (
    <div className="signin-root">
      <style>{DESIGN_CSS}</style>
      <div className="shell">
        {/* left — reassurance / brand */}
        <div className="leftpanel px-9 py-9 flex flex-col" style={{ background: "var(--success-50)", borderRight: "1px solid #cdd6bd" }}>
          <a href="/scholar-path" className="inline-flex items-center gap-2.5 focus-ring" style={{ textDecoration: "none" }}>
            <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: "linear-gradient(140deg,var(--moss),var(--forest-2))" }}><I name="shield-check" cls="w-[18px] h-[18px]" /></span>
            <span className="font-display text-[20px] text-ink">VisaGenius</span>
          </a>
          <div className="my-auto">
            <h2 className="font-display text-[30px] leading-[1.12] text-ink">Your case is right where you left it.</h2>
            <p className="mt-3 text-[13.5px] leading-relaxed text-ink-2">Pick up your EB-1A petition, message your attorney, and track your filing — all in one place.</p>
            <div className="mt-6 space-y-3">
              {[["save", "Your progress auto-saves at every step"], ["lock", "Bank-level encryption — your data is never sold"], ["message-circle", "Your attorney and case team, one message away"]].map(([ic, t]) => (
                <div key={t} className="flex items-center gap-2.5 text-[12.5px] text-ink-1"><I name={ic} cls="w-4 h-4 shrink-0" /> {t}</div>
              ))}
            </div>
          </div>
          <div className="mt-8 pt-5 text-[12.5px] text-ink-2" style={{ borderTop: "1px solid #cdd6bd" }}>
            New here? <a href="/scholar-path" className="quiet-link focus-ring" style={{ color: "var(--accent-700)", textDecoration: "none" }}>Check your eligibility free →</a>
          </div>
        </div>

        {/* right — login form */}
        <div className="px-10 py-9 flex flex-col justify-center" style={{ background: "var(--surface)" }}>
          {step === "code" ? (
            <div className="max-w-[360px] mx-auto w-full fade">
              <span className="w-14 h-14 rounded-full flex items-center justify-center text-white mb-4" style={{ background: "var(--success)" }}><I name="mail-check" cls="w-7 h-7" /></span>
              <h1 className="font-display text-[28px] text-ink leading-tight">Enter your code.</h1>
              <p className="mt-2 text-[13.5px] text-ink-2 leading-relaxed">We sent a 6-digit code to <b className="text-ink-1">{email}</b>. It expires in 10 minutes.</p>
              <div className="flex gap-2 mt-5">
                {code.map((d, i) => (
                  <input key={i} ref={codeRefs[i]} className="g-input text-center font-sans text-[20px]" style={{ width: 48, padding: "12px 0" }} inputMode="numeric" maxLength={1} value={d} onChange={e => setDigit(i, e.target.value)} onKeyDown={e => onCodeKey(i, e)} onPaste={e => onCodePaste(i, e)} autoFocus={i === 0} />
                ))}
              </div>
              {error && <div className="mt-3 text-[12.5px]" style={{ color: "var(--accent-700)" }}>{error}</div>}
              <button className="btn-primary focus-ring w-full h-12 text-[15px] mt-5 inline-flex items-center justify-center gap-2" disabled={!codeFull || verifying} style={(!codeFull || verifying) ? { opacity: .45 } : {}} onClick={verifyCode}>
                {verifying ? "Verifying…" : <>Verify &amp; log in <I name="arrow-right" cls="w-4 h-4" /></>}
              </button>
              <div className="flex items-center justify-between mt-3.5 text-[12.5px]">
                <button className="text-ink-2 quiet-link focus-ring inline-flex items-center gap-1.5" onClick={() => { setStep("email"); setCode(["", "", "", "", "", ""]); setError(""); }}><I name="arrow-left" cls="w-3.5 h-3.5" /> Change email</button>
                <button className="text-ink-2 quiet-link focus-ring" style={{ color: "var(--accent-700)" }} onClick={requestCode} disabled={sending}>{sending ? "Sending…" : "Resend code"}</button>
              </div>
            </div>
          ) : (
            <div className="max-w-[360px] mx-auto w-full fade">
              {showSignedOut && (
                <div className="mb-4 rounded-xl px-3.5 py-2.5 flex items-center gap-2 fade" style={{ background: "var(--success-50)", border: "1px solid #cdd6bd", color: "var(--success-700)" }}>
                  <I name="check-circle-2" cls="w-4 h-4 shrink-0" />
                  <span className="text-[13px]">Signed out. Your case is safely saved.</span>
                </div>
              )}
              <h1 className="font-display text-[30px] text-ink leading-tight">Welcome back.</h1>
              <p className="mt-1.5 text-[13.5px] text-ink-2">Log in to continue your EB-1A petition.</p>

              <div className="mt-6">
                <button
                  className="btn-sso focus-ring w-full h-11 inline-flex items-center justify-center gap-2.5 text-[14px]"
                  onClick={continueWithGoogle}
                  disabled={!googleAvailable || googleBusy}
                  title={!googleAvailable ? "Google sign-in is not configured on this deployment yet." : undefined}
                  style={(!googleAvailable || googleBusy) ? { opacity: 0.6, cursor: "not-allowed" } : {}}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
                  {googleBusy ? "Continuing…" : "Continue with Google"}
                </button>
              </div>

              <div className="flex items-center gap-3 my-5"><span className="h-px flex-1" style={{ background: "var(--hair)" }}></span><span className="text-[12.5px] font-sans uppercase tracking-wider text-ink-3">or</span><span className="h-px flex-1" style={{ background: "var(--hair)" }}></span></div>

              <label className="font-sans text-[12.5px] uppercase tracking-[0.14em] text-ink-3">Email</label>
              <input className="g-input mt-1.5" type="email" placeholder="you@university.edu" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && valid) requestCode(); }} />
              {error && <div className="mt-3 text-[12.5px]" style={{ color: "var(--accent-700)" }}>{error}</div>}
              <button className="btn-primary focus-ring w-full h-12 text-[15px] mt-4 inline-flex items-center justify-center gap-2" disabled={!valid || sending} style={(!valid || sending) ? { opacity: .45 } : {}} onClick={requestCode}>
                <I name="wand-2" cls="w-4 h-4" /> {sending ? "Sending…" : "Email me a sign-in code"}
              </button>
              <p className="text-[12.5px] text-ink-3 mt-2.5 text-center">No password needed — we'll email you a 6-digit code to log in.</p>

              <p className="text-[12.5px] text-ink-3 mt-6 text-center leading-relaxed">By continuing you agree to our <a href="/legal#terms" className="quiet-link" style={{color:"var(--ink-2)",textDecoration:"none"}}>Terms</a> and <a href="/legal#privacy" className="quiet-link" style={{color:"var(--ink-2)",textDecoration:"none"}}>Privacy Policy</a>.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
